/* global $, config, APP */

import React from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';

import { getActiveDOMDocument } from '../../../react/features/base/config/functions.any';
import { i18next } from '../../../react/features/base/i18n';
import { MHTrackEvents } from '../../../react/features/base/lib-meet-hour';
import { LocalVideoContextMenu, showLocalVideoContextMenu } from '../../../react/features/filmstrip/components/web/LocalVideoContextMenu';
import Thumbnail from '../../../react/features/filmstrip/components/web/Thumbnail';
import { LAYOUTS, shouldDisplayTileView } from '../../../react/features/video-layout';
import UIEvents from '../../../service/UI/UIEvents';

import SmallVideo from './SmallVideo';

/**
 *
 */
export default class LocalVideo extends SmallVideo {
    /**
     *
     * @param {*} emitter
     * @param {*} streamEndedCallback
     */
    constructor(emitter, streamEndedCallback) {
        super();
        this.videoSpanId = 'localVideoContainer';
        this.streamEndedCallback = streamEndedCallback;
        this.container = this.createContainer();
        this.$container = $(this.container);
        this.isLocal = true;
        this._disposed = false;

        // this._setThumbnailSize();
        this.updateDOMLocation();
        this.renderThumbnail();

        this.localVideoId = null;
        this.bindHoverHandler();
        this.emitter = emitter;

        Object.defineProperty(this, 'id', {
            get() {
                return APP.conference.getMyUserId();
            }
        });
        this.initBrowserSpecificProperties();

        this.container.onclick = this._onContainerClick;
        this._lastAppliedObjectFit = null;

        // Subscribe to store to listen for objectFit changes
        this._unsubscribe = APP.store.subscribe(() => {
            if (this._disposed) {
                return;
            }

            this._updateObjectFitStyle();
            this.updateZoomTransform();
        });
    }

    /**
     * Updates the objectFit style of the local video element if it exists.
     */
    _updateObjectFitStyle() {
        const videoEl = this.container && this.container.querySelector('video');

        if (!videoEl) {
            return;
        }

        const state = APP.store.getState();
        const { filmStripView, objectFit } = state['features/base/conference'];
        const nextObjectFit = filmStripView === LAYOUTS.TILE_VIEW ? objectFit : 'cover';

        if (this._lastAppliedObjectFit !== nextObjectFit) {
            videoEl.style.objectFit = nextObjectFit;
            this._lastAppliedObjectFit = nextObjectFit;
        }
    }

    /**
     *
     */
    createContainer() {
        const state = APP.store.getState();
        const { filmStripView, objectFit } = state['features/base/conference'];
        const containerSpan = document.createElement('span');

        containerSpan.classList.add('videocontainer');
        containerSpan.id = this.videoSpanId;
        containerSpan.style.objectFit = filmStripView === LAYOUTS.TILE_VIEW ? objectFit : 'cover';


        return containerSpan;
    }

    /**
     * Renders the thumbnail.
     */
    renderThumbnail(isHovered = false) {
        // Do not (re)render after the instance has been removed. root.unmount()
        // flushes React synchronously and hover/layout callbacks could otherwise
        // re-enter here and render into a torn-down root.
        if (this._disposed || !this.container) {
            return;
        }

        // Check if a root already exists to avoid recreating
        if (!this.root) {
            this.root = createRoot(this.container);
        }

        // Setup context menu handler
        if (!config.disableLocalVideoFlip) {
            this.container.oncontextmenu = event => {
                showLocalVideoContextMenu(event, this.videoSpanId);
            };
        }

        // Get or create portal container for context menus
        let portalContainer = document.getElementById('context-menu-portal');

        if (!portalContainer) {
            portalContainer = document.createElement('div');
            portalContainer.id = 'context-menu-portal';
            document.body.appendChild(portalContainer);
        }

        this.root.render(
            <Provider store = { APP.store }>
                <I18nextProvider i18n = { i18next }>
                    <Thumbnail
                        isHovered = { isHovered }
                        participantID = { this.id } />
                    {createPortal(
                        <LocalVideoContextMenu
                            participantId = { this.id }
                            setFlipX = { this.setFlipX.bind(this) }
                            showParticipantName = { true }
                            videoSpanId = { this.videoSpanId } />,
                        portalContainer
                    )}
                </I18nextProvider>
            </Provider>
        );
    }

    /**
     *
     * @param {*} stream
     */
    changeVideo(stream) {
        this.localVideoId = `localVideo_${stream.getId()}`;

        const isVideo = stream.videoType !== 'desktop';
        const settings = APP.store.getState()['features/base/settings'];

        this.setFlipX(isVideo ? settings.localFlipX : false);

        const endedHandler = () => {
            this._notifyOfStreamEnded();
            stream.off(MHTrackEvents.LOCAL_TRACK_STOPPED, endedHandler);
        };

        stream.on(MHTrackEvents.LOCAL_TRACK_STOPPED, endedHandler);
    }

    /**
     * Notify any subscribers of the local video stream ending.
     *
     * @private
     * @returns {void}
     */
    _notifyOfStreamEnded() {
        if (this.streamEndedCallback) {
            this.streamEndedCallback(this.id);
        }
    }

    /**
     * Shows or hides the local video container.
     *
     * @param {boolean} true - To make the local video container visible, false
     * otherwise.
     */
    setVisible(visible) {
        // We toggle the hidden class as an indication to other interested parties
        // that this container has been hidden on purpose.
        this.$container.toggleClass('hidden');

        // We still show/hide it as we need to overwrite the style property if we
        // want our action to take effect. Toggling the display property through
        // the above css class didn't succeed in overwriting the style.
        if (visible) {
            this.$container.show();
        } else {
            this.$container.hide();
        }
    }

    /**
     * Sets the flipX state of the video.
     *
     * @param val - {boolean} True for flipped otherwise false;.
     */
    setFlipX(val) {
        this.localFlipX = val;
        this.emitter.emit(UIEvents.LOCAL_FLIPX_CHANGED, val);
        if (!this.localVideoId) {
            return;
        }
        if (val) {
            this.selectVideoElement().addClass('flipVideoX');
        } else {
            this.selectVideoElement().removeClass('flipVideoX');
        }
        this.updateZoomTransform();
    }

    /**
     * Places the {@code LocalVideo} in the DOM based on the current video layout.
     *
     * @returns {void}
     */
    updateDOMLocation() {
        if (!this.container) {
            return;
        }

        const state = APP.store.getState();
        const activeWindow = getActiveDOMDocument(state);
        const appendTarget = shouldDisplayTileView(APP.store.getState())
            ? activeWindow.getElementById('localVideoTileViewContainer')
            : activeWindow.getElementById('filmstripLocalVideoThumbnail');

        if (!appendTarget) {
            return;
        }

        // Avoid unnecessary remove/append cycles because Safari can blink the
        // local video element when it is re-parented during frequent layout
        // updates (screen-share start/stop, tile adjustments, visibility flips).
        if (this.container.parentElement !== appendTarget) {
            if (this.container.parentElement) {
                this.container.parentElement.removeChild(this.container);
            }

            appendTarget.appendChild(this.container);
        }

        // Update objectFit style after moving in DOM
        this._updateObjectFitStyle();
    }

    /**
     * Clean up store subscription on removal
     */
    remove() {
        this._disposed = true;

        if (this._unsubscribe) {
            this._unsubscribe();
            this._unsubscribe = null;
        }
        if (super.remove) {
            super.remove();
        }
    }
}
